import { NekoStaffSdk } from 'neko-common';
import { nekoStaff } from 'public-contracts';
import { configs } from '../configs';

type StaffBadge = nekoStaff.StaffBadge;
type StaffBadgeEarned = nekoStaff.StaffBadgeEarned;
type StaffBadgeCreateEntry = nekoStaff.StaffBadgeEarned;

const sdk = new NekoStaffSdk(configs.apiRoot);

export class StaffBadgeService {
  private apiRoot = configs.apiRoot;

  async getStaffBadges(): Promise<StaffBadge[]> {
    try {
      const response = await fetch(`${this.apiRoot}/staff_badges`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add auth header if needed
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch staff badges: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching staff badges:', error);
      throw error;
    }
  }

  async getStaffBadgeById(id: string): Promise<StaffBadge> {
    try {
      const response = await fetch(`${this.apiRoot}/staff_badges/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch staff badge with ID ${id}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching staff badge with ID ${id}:`, error);
      throw error;
    }
  }

  async getStaffBadgesByUserId(userId: string): Promise<StaffBadgeEarned[]> {
    try {
      // For now, return empty array since we don't have earned badges endpoint
      return [];
    } catch (error) {
      console.error(`Error fetching staff badges for user ${userId}:`, error);
      throw error;
    }
  }

  async createStaffBadge(
    entry: StaffBadgeCreateEntry
  ): Promise<{ insertedId: string }> {
    try {
      const response = await fetch(`${this.apiRoot}/staff_badges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      if (!response.ok) {
        throw new Error(`Failed to create staff badge: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating staff badge:', error);
      throw error;
    }
  }

  async updateStaffBadge(
    id: string,
    entry: StaffBadgeEarned
  ): Promise<StaffBadgeEarned> {
    try {
      const response = await fetch(`${this.apiRoot}/staff_badges/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to update staff badge ${id}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating staff badge ${id}:`, error);
      throw error;
    }
  }

  async deleteStaffBadge(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiRoot}/staff_badges/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to delete staff badge ${id}: ${response.statusText}`
        );
      }
    } catch (error) {
      console.error(`Error deleting staff badge ${id}:`, error);
      throw error;
    }
  }

  // Helper method to get earned badges for a user
  async getEarnedBadges(userId: string): Promise<StaffBadgeEarned[]> {
    try {
      const userBadges = await this.getStaffBadgesByUserId(userId);
      return userBadges;
    } catch (error) {
      console.error(`Error getting earned badges for ${userId}:`, error);
      return [];
    }
  }

  // Helper method to get available badges (not yet earned)
  async getAvailableBadges(userId: string): Promise<StaffBadge[]> {
    try {
      const allBadges = await this.getStaffBadges();
      const userBadges = await this.getStaffBadgesByUserId(userId);
      const earnedBadgeIds = userBadges.map((badge) => badge.badgeId);
      return allBadges.filter((badge) => !earnedBadgeIds.includes(badge.id));
    } catch (error) {
      console.error(`Error getting available badges for ${userId}:`, error);
      return [];
    }
  }

  // Helper method to get badges by category
  async getBadgesByCategory(category: string): Promise<StaffBadge[]> {
    try {
      const allBadges = await this.getStaffBadges();
      return allBadges.filter((badge) => badge.category === category);
    } catch (error) {
      console.error(`Error getting badges by category ${category}:`, error);
      return [];
    }
  }

  // Helper method to award a badge to a user
  async awardBadge(
    userId: string,
    badgeId: string,
    awardedBy?: string
  ): Promise<{ insertedId: string }> {
    const badgeEntry: StaffBadgeCreateEntry = {
      id: '', // Will be generated by the server
      userId,
      badgeId,
      earnedAt: new Date(),
      awardedBy,
    };

    return await this.createStaffBadge(badgeEntry);
  }
}

export const staffBadgeService = new StaffBadgeService();
